import {
    createContext,
    Dispatch,
    SetStateAction,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { useSettings } from '@/components/settings/SettingsProvider'
import { ApiBrandBenefit } from '@storefronts/common-types'
import { v4 as uuid } from 'uuid'
import { R } from '@/modules/api/actions'
import { toast } from '@/components/ui/sonner'

export type DraggableBrandBenefit = Omit<ApiBrandBenefit, 'id'> & {
    id?: number
    hash: string
}

interface BrandBenefitsContextValue {
    benefits: DraggableBrandBenefit[]
    setBenefits: Dispatch<SetStateAction<DraggableBrandBenefit[]>>
    currentBenefit: DraggableBrandBenefit
    setCurrentBenefit: Dispatch<SetStateAction<DraggableBrandBenefit>>
    handleSave: () => Promise<void>
    discardChanges: () => void
    isLoading: boolean
    hasChanges: boolean
    isEditing: boolean
    setIsEditing: Dispatch<SetStateAction<boolean>>
    dialogOpen: boolean
    setDialogOpen: Dispatch<SetStateAction<boolean>>
}

const BrandBenefitsContext = createContext<BrandBenefitsContextValue>({
    benefits: [],
    setBenefits: () => {},
    currentBenefit: {} as DraggableBrandBenefit,
    setCurrentBenefit: () => {},
    handleSave: async () => {},
    discardChanges: () => {},
    isLoading: false,
    hasChanges: false,
    isEditing: false,
    setIsEditing: () => {},
    dialogOpen: false,
    setDialogOpen: () => {},
})

export const BrandBenefitsProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const { settings, refreshSettings } = useSettings()
    const [benefits, setBenefits] = useState<DraggableBrandBenefit[]>([])
    const [originalBenefits, setOriginalBenefits] = useState<
        DraggableBrandBenefit[]
    >([])
    const [currentBenefit, setCurrentBenefit] = useState<DraggableBrandBenefit>(
        {
            type: 'SHIPPING',
            description: '',
            rank: 1,
            hash: uuid(),
            isAccountBanner: false,
            isAddToCartButtonBenefit: false,
            isBuyNowButtonBenefit: false,
            isCheckoutButtonBenefit: false,
        },
    )
    const isLoading = !settings
    const [isEditing, setIsEditing] = useState(false)
    const [dialogOpen, setDialogOpen] = useState(false)

    const transformBenefitToSortableBenefit = (
        benefit: ApiBrandBenefit,
    ): DraggableBrandBenefit => {
        return {
            ...benefit,
            hash: uuid(),
        }
    }

    useEffect(() => {
        if (settings?.brandBenefits) {
            const sortableBenefits = settings?.brandBenefits
                .sort((a, b) => a.rank - b.rank)
                .map(transformBenefitToSortableBenefit)
            setOriginalBenefits(sortableBenefits)
            setBenefits(sortableBenefits)
        }
    }, [settings])

    // Track changes by comparing current benefits to original benefits
    const hasChanges = useMemo(
        () => JSON.stringify(benefits) !== JSON.stringify(originalBenefits),
        [benefits, originalBenefits],
    )

    const handleSave = async () => {
        if (!hasChanges) return

        try {
            const response = await R.bulkReplaceBrandBenefits({
                brandBenefits: benefits,
            })

            if (response.isError) {
                toast.error(response.message || 'Failed to save brand benefits')
                return
            }

            await refreshSettings()
            toast.success('Brand benefits saved successfully')
        } catch (error: any) {
            console.error('Error saving brand benefits:', error)
            toast.error(
                error.message ||
                    'An error occurred while saving brand benefits. Please try again.',
            )
            throw error
        }
    }

    const discardChanges = () => {
        setBenefits(originalBenefits)
    }

    const value = {
        benefits,
        setBenefits,
        currentBenefit,
        setCurrentBenefit,
        handleSave,
        discardChanges,
        isLoading,
        hasChanges,
        isEditing,
        setIsEditing,
        dialogOpen,
        setDialogOpen,
    }

    return (
        <BrandBenefitsContext.Provider value={value}>
            {children}
        </BrandBenefitsContext.Provider>
    )
}

export const useBrandBenefits = () => {
    const context = useContext(BrandBenefitsContext)
    if (context === undefined) {
        throw new Error(
            'useBrandBenefits must be used within a BrandBenefitsProvider',
        )
    }
    return context
}
